import { deltaE00 } from './colour';
import { rgbToLab } from './palette';
import type { ColourMetric, DitherMethod, PaletteEntry, Rgb } from './types';

export const DITHER_METHODS: DitherMethod[] = [
  { key: 'none', kind: 'none' },
  {
    key: 'floyd-steinberg',
    label: 'Floyd-Steinberg',
    kind: 'diffusion',
    matrix: [
      [0, 0, 0, 7, 0],
      [0, 3, 5, 1, 0],
      [0, 0, 0, 0, 0],
    ],
    divisor: 16,
  },
  {
    key: 'burkes',
    label: 'Burkes',
    kind: 'diffusion',
    matrix: [
      [0, 0, 0, 8, 4],
      [2, 4, 8, 4, 2],
      [0, 0, 0, 0, 0],
    ],
    divisor: 32,
  },
  {
    key: 'atkinson',
    label: 'Atkinson',
    kind: 'diffusion',
    matrix: [
      [0, 0, 0, 1, 1],
      [0, 1, 1, 1, 0],
      [0, 0, 1, 0, 0],
    ],
    divisor: 8,
  },
  {
    key: 'stucki',
    label: 'Stucki',
    kind: 'diffusion',
    matrix: [
      [0, 0, 0, 8, 4],
      [2, 4, 8, 4, 2],
      [1, 2, 4, 2, 1],
    ],
    divisor: 42,
  },
  {
    key: 'jarvis',
    label: 'Jarvis-Judice-Ninke',
    kind: 'diffusion',
    matrix: [
      [0, 0, 0, 7, 5],
      [3, 5, 7, 5, 3],
      [1, 3, 5, 3, 1],
    ],
    divisor: 48,
  },
  {
    key: 'sierra-lite',
    label: 'Sierra Lite',
    kind: 'diffusion',
    matrix: [
      [0, 0, 0, 2, 0],
      [0, 1, 1, 0, 0],
      [0, 0, 0, 0, 0],
    ],
    divisor: 4,
  },
  {
    key: 'blue-noise',
    label: 'Blue noise',
    kind: 'ordered',
    // Void and cluster ranks: the thresholds spread out instead of lining up, so a gradient gets
    // texture rather than the crosshatch a Bayer matrix leaves behind.
    matrix: [
      [28, 253, 127, 74, 143, 114, 54, 20, 99, 38, 185, 163, 51, 131, 18, 189],
      [155, 47, 201, 60, 241, 12, 82, 153, 136, 225, 110, 255, 7, 179, 142, 88],
      [223, 105, 180, 24, 167, 210, 194, 234, 175, 59, 25, 148, 71, 230, 203, 39],
      [118, 2, 138, 229, 94, 121, 33, 68, 3, 209, 86, 188, 123, 101, 56, 245],
      [164, 65, 191, 76, 44, 151, 249, 130, 96, 165, 239, 45, 221, 14, 171, 80],
      [208, 128, 252, 215, 10, 108, 183, 50, 197, 113, 134, 32, 157, 199, 140, 29],
      [49, 97, 21, 159, 178, 84, 219, 27, 232, 13, 75, 251, 61, 90, 112, 243],
      [176, 36, 144, 116, 58, 242, 137, 66, 154, 212, 177, 104, 187, 217, 8, 228],
      [87, 193, 236, 72, 205, 16, 166, 40, 91, 120, 52, 146, 22, 129, 69, 150],
      [57, 106, 222, 4, 186, 102, 124, 226, 192, 247, 1, 206, 235, 43, 168, 202],
      [125, 26, 162, 132, 46, 256, 79, 173, 19, 139, 73, 161, 83, 115, 254, 15],
      [214, 77, 246, 93, 152, 31, 198, 55, 109, 35, 100, 224, 195, 30, 98, 184],
      [141, 41, 174, 62, 211, 117, 231, 147, 216, 244, 181, 48, 133, 64, 156, 233],
      [53, 111, 196, 238, 17, 135, 70, 5, 89, 158, 119, 9, 248, 172, 81, 11],
      [207, 149, 6, 85, 182, 103, 42, 170, 204, 63, 23, 145, 200, 37, 220, 122],
      [169, 95, 227, 34, 160, 218, 250, 190, 126, 237, 78, 213, 92, 107, 240, 67]
    ],
  },
  {
    key: 'bayer-2x2',
    label: 'Bayer 2×2',
    kind: 'ordered',
    matrix: [
      [1, 3],
      [4, 2],
    ],
  },
  {
    key: 'bayer-4x4',
    label: 'Bayer 4×4',
    kind: 'ordered',
    matrix: [
      [1, 9, 3, 11],
      [13, 5, 15, 7],
      [4, 12, 2, 10],
      [16, 8, 14, 6],
    ],
  },
  {
    key: 'ordered-3x3',
    label: 'Ordered 3×3',
    kind: 'ordered',
    matrix: [
      [1, 7, 4],
      [5, 8, 3],
      [6, 2, 9],
    ],
  },
];

export function ditherMethod(key: string): DitherMethod {
  return DITHER_METHODS.find((m) => m.key === key) ?? DITHER_METHODS[0];
}

/**
 * How many shortlisted candidates CIEDE2000 gets to rank. Measured against brute force over a
 * dense sample of the RGB cube: 16 reproduces the full ranking exactly, 12 costs at most 0.8
 * CIEDE2000 units on 0.02% of colours. See `twoClosestPerceptual`.
 */
export const DE2000_SHORTLIST = 16;

export class NearestFinder {
  private readonly cache = new Map<number, number>();
  private readonly chroma: number[];
  private readonly candidates: number[];

  /** `candidates` narrows the search to those entry indices; results still index `entries`. */
  constructor(
    private readonly entries: PaletteEntry[],
    private readonly metric: ColourMetric,
    candidates?: number[],
  ) {
    this.chroma = entries.map((e) => Math.hypot(e.lab[1], e.lab[2]));
    this.candidates = candidates ?? entries.map((_, index) => index);
  }

  /**
   * Two closest entries; second is -1 when the palette has a single entry. Distances are only
   * comparable within one metric, squared for rgb/lab and plain CIEDE2000 units for de2000.
   */
  twoClosest(r: number, g: number, b: number): [number, number, number, number] {
    const cr = clamp255(r);
    const cg = clamp255(g);
    const cb = clamp255(b);
    const target: Rgb = this.metric === 'rgb' ? [cr, cg, cb] : rgbToLab([cr, cg, cb]);
    if (this.metric === 'de2000') return this.twoClosestPerceptual(target);

    let best = -1;
    let second = -1;
    let bestDist = Infinity;
    let secondDist = Infinity;
    for (const i of this.candidates) {
      const c = this.metric === 'lab' ? this.entries[i].lab : this.entries[i].rgb;
      const dr = target[0] - c[0];
      const dg = target[1] - c[1];
      const db = target[2] - c[2];
      const d = dr * dr + dg * dg + db * db;
      if (d < bestDist) {
        second = best;
        secondDist = bestDist;
        best = i;
        bestDist = d;
      } else if (d < secondDist) {
        second = i;
        secondDist = d;
      }
    }
    return [best, bestDist, second, secondDist];
  }

  /**
   * CIEDE2000 costs several times a plain distance, which at a full artwork with dithering runs to
   * seconds per rebuild. So a cheap proxy shortlists and CIEDE2000 only ranks those. The proxy
   * carries CIEDE2000's SL and SC denominators but skips its hue rotation and trigonometry:
   * plain Lab is not good enough here, it drops the true winner on 2% of colours even at 32
   * candidates, because it misjudges exactly the saturated and dark regions this palette crowds.
   */
  private twoClosestPerceptual(target: Rgb): [number, number, number, number] {
    const shortlist: number[] = [];
    const proxies: number[] = [];
    const targetChroma = Math.hypot(target[1], target[2]);
    for (const i of this.candidates) {
      const lab = this.entries[i].lab;
      const meanLOffset = ((target[0] + lab[0]) / 2 - 50) ** 2;
      const sl = 1 + (0.015 * meanLOffset) / Math.sqrt(20 + meanLOffset);
      const sc = 1 + 0.045 * ((targetChroma + this.chroma[i]) / 2);
      const dl = (target[0] - lab[0]) / sl;
      const da = (target[1] - lab[1]) / sc;
      const db = (target[2] - lab[2]) / sc;
      const d = dl * dl + da * da + db * db;
      if (shortlist.length === DE2000_SHORTLIST && d >= proxies[shortlist.length - 1]) continue;
      let at = shortlist.length;
      while (at > 0 && proxies[at - 1] > d) at--;
      shortlist.splice(at, 0, i);
      proxies.splice(at, 0, d);
      if (shortlist.length > DE2000_SHORTLIST) {
        shortlist.pop();
        proxies.pop();
      }
    }

    let best = -1;
    let second = -1;
    let bestDist = Infinity;
    let secondDist = Infinity;
    for (const i of shortlist) {
      const d = deltaE00(target, this.entries[i].lab);
      if (d < bestDist) {
        second = best;
        secondDist = bestDist;
        best = i;
        bestDist = d;
      } else if (d < secondDist) {
        second = i;
        secondDist = d;
      }
    }
    return [best, bestDist, second, secondDist];
  }

  closest(r: number, g: number, b: number): number {
    const key = (clamp255(r) << 16) | (clamp255(g) << 8) | clamp255(b);
    const hit = this.cache.get(key);
    if (hit !== undefined) return hit;
    const index = this.twoClosest(r, g, b)[0];
    this.cache.set(key, index);
    return index;
  }
}

function clamp255(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}

/** One section as the quantiser runs it. */
export interface QuantizeSection {
  dither: DitherMethod;
  /** How much of the quantisation error to pass on, 0 turning dithering off entirely. */
  strength: number;
  /** Indices into `entries` this section may pick: a flat section only gets the normal tones. */
  candidates: number[];
}

export interface QuantizeOptions {
  width: number;
  height: number;
  rgba: Uint8ClampedArray;
  entries: PaletteEntry[];
  metric: ColourMetric;
  alphaThreshold: number;
  serpentine: boolean;
  sections: QuantizeSection[];
  /** Index into `sections` per pixel, or null when section 0 covers the whole artwork. */
  owners: Uint8Array | null;
}

/**
 * Keeps a channel in range without rounding. Error diffusion has to work from the clamped value:
 * a colour the palette cannot reach leaves error behind on every pixel, and feeding the raw sum
 * back in lets it grow without limit until whole regions land on whichever block is most extreme
 * in that direction.
 */
function clampChannel(value: number): number {
  return value < 0 ? 0 : value > 255 ? 255 : value;
}

type SectionRun = QuantizeSection & { finder: NearestFinder; kind: DitherMethod['kind'] };

function prepareSections(options: QuantizeOptions): SectionRun[] {
  const finders = new Map<number[], NearestFinder>();
  return options.sections.map((section) => {
    let finder = finders.get(section.candidates);
    if (!finder) {
      finder = new NearestFinder(options.entries, options.metric, section.candidates);
      finders.set(section.candidates, finder);
    }
    const strength = Math.max(0, Math.min(1, section.strength));
    const { dither } = section;
    const diffuses = dither.kind === 'diffusion' && dither.matrix && dither.divisor;
    const orders = dither.kind === 'ordered' && dither.matrix;
    const kind = strength > 0 && (diffuses || orders) ? dither.kind : 'none';
    return { ...section, strength, finder, kind };
  });
}

/** Per pixel index into `entries`, or -1 where the composite is transparent. */
export function quantize(options: QuantizeOptions): Int32Array {
  const { width, height, rgba, entries, alphaThreshold, serpentine, owners } = options;
  const out = new Int32Array(width * height).fill(-1);
  if (entries.length === 0) return out;

  const runs = prepareSections(options);
  const work = runs.some((run) => run.kind === 'diffusion') ? new Float32Array(width * height * 3) : null;
  if (work) {
    for (let i = 0; i < width * height; i++) {
      work[i * 3] = rgba[i * 4];
      work[i * 3 + 1] = rgba[i * 4 + 1];
      work[i * 3 + 2] = rgba[i * 4 + 2];
    }
  }

  for (let y = 0; y < height; y++) {
    // Serpentine: flip the scan direction on odd rows so diffused error cannot drift one way.
    const reverse = work !== null && serpentine && y % 2 === 1;
    for (let step = 0; step < width; step++) {
      const x = reverse ? width - 1 - step : step;
      const p = y * width + x;
      if (rgba[p * 4 + 3] < alphaThreshold) continue;
      const owner = owners ? owners[p] : 0;
      const run = runs[owner];

      if (run.kind === 'none') {
        out[p] = run.finder.closest(rgba[p * 4], rgba[p * 4 + 1], rgba[p * 4 + 2]);
        continue;
      }

      const matrix = run.dither.matrix!;
      if (run.kind === 'ordered') {
        const [best, bestDist, second, secondDist] = run.finder.twoClosest(
          rgba[p * 4],
          rgba[p * 4 + 1],
          rgba[p * 4 + 2],
        );
        if (second < 0) {
          out[p] = best;
          continue;
        }
        // How ambiguous the match is (0 = exact) scaled onto the threshold matrix range,
        // so near-ties alternate between the two candidates on the matrix pattern.
        const scale = matrix[0].length * matrix.length + 1;
        const ambiguity = (bestDist * scale * run.strength) / (bestDist + secondDist);
        out[p] = ambiguity > matrix[y % matrix.length][x % matrix[0].length] ? second : best;
        continue;
      }

      const r = clampChannel(work![p * 3]);
      const g = clampChannel(work![p * 3 + 1]);
      const b = clampChannel(work![p * 3 + 2]);
      const index = run.finder.closest(r, g, b);
      out[p] = index;
      const chosen = entries[index].rgb;
      const er = (r - chosen[0]) * run.strength;
      const eg = (g - chosen[1]) * run.strength;
      const eb = (b - chosen[2]) * run.strength;
      for (let dy = 0; dy < matrix.length; dy++) {
        for (let dx = 0; dx < matrix[dy].length; dx++) {
          const weight = matrix[dy][dx];
          if (weight === 0) continue;
          const neighbourX = reverse ? x - (dx - 2) : x + dx - 2;
          const neighbourY = y + dy;
          if (neighbourX < 0 || neighbourX >= width || neighbourY >= height) continue;
          const neighbour = neighbourY * width + neighbourX;
          // Error stays inside its section, or a flat layer's edge picks up the photo's noise.
          if (owners && owners[neighbour] !== owner) continue;
          const share = weight / run.dither.divisor!;
          work![neighbour * 3] += er * share;
          work![neighbour * 3 + 1] += eg * share;
          work![neighbour * 3 + 2] += eb * share;
        }
      }
    }
  }
  return out;
}
